import { format } from 'date-fns';

import { FiscalEventInterface } from '@app/interface/fiscal-event';
import { InvoiceInterface } from '@app/interface/invoice';
import { PersonalEventInterface } from '@app/interface/personal-event';

/** Le tre famiglie di contenuto della bacheca, corrispondono ai chip di filtro. */
export type BoardCategory = 'FATTURE' | 'FISCO' | 'ATTIVITA';

/**
 * Una fattura compare due volte nel calendario con significati diversi:
 * il giorno in cui è stata emessa e il giorno in cui ci si aspetta l'incasso.
 */
export type BoardItemKind =
  | 'INVOICE_ISSUED'
  | 'INVOICE_PAYMENT'
  | 'FISCAL'
  | 'PERSONAL';

export const CATEGORY_OF_KIND: Record<BoardItemKind, BoardCategory> = {
  INVOICE_ISSUED: 'FATTURE',
  INVOICE_PAYMENT: 'FATTURE',
  FISCAL: 'FISCO',
  PERSONAL: 'ATTIVITA',
};

/** Elemento normalizzato: è l'unica cosa che la griglia e l'agenda conoscono. */
export interface BoardItem {
  /** Chiave stabile per `track`: la coppia tipo+id è unica. */
  key: string;
  kind: BoardItemKind;
  category: BoardCategory;
  /** 'yyyy-MM-dd', la chiave con cui si raggruppa per giorno. */
  dayKey: string;
  title: string;
  subtitle?: string;
  amount?: number;
  icon: string;
  /** Già incassata o già pagata: la voce perde enfasi visiva. */
  settled: boolean;
  important: boolean;

  invoice?: InvoiceInterface;
  fiscalEvent?: FiscalEventInterface;
  personalEvent?: PersonalEventInterface;
}

/**
 * Normalizza una data in chiave giornaliera.
 *
 * Le date del backend arrivano come 'YYYY-MM-DD': tagliare la stringa evita
 * di costruire un Date e di incappare negli scivolamenti di fuso (una
 * DateField interpretata come UTC può retrocedere di un giorno).
 */
export function toDayKey(
  value: string | Date | null | undefined,
): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value.length >= 10 ? value.slice(0, 10) : null;
  }
  return format(value, 'yyyy-MM-dd');
}

/**
 * I DecimalField del backend arrivano come stringhe ('200.00'), anche se
 * l'interfaccia li dichiara number: sommarli cosi' com'e' li concatenerebbe
 * ('0200.00102.00') e il pipe currency andrebbe in errore, bloccando il
 * render dell'intero calendario. Qui diventano numeri veri.
 */
export function toAmount(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : undefined;
}

export function invoiceIssuedItem(invoice: InvoiceInterface): BoardItem | null {
  const dayKey = toDayKey(invoice.emission_date);
  if (!dayKey) {
    return null;
  }
  return {
    key: `INVOICE_ISSUED-${invoice.id}`,
    kind: 'INVOICE_ISSUED',
    category: 'FATTURE',
    dayKey,
    title: invoice.company?.company_name || 'Fattura',
    subtitle: `Fattura n. ${invoice.progressive_number}`,
    amount: toAmount(invoice.gross_amount),
    icon: 'receipt_long',
    settled: false,
    important: false,
    invoice,
  };
}

/**
 * L'incasso atteso ha senso solo finché la fattura non è saldata: una volta
 * incassata la voce si sposta sul giorno dell'incasso reale
 * (`invoiceSettledPaymentItem`), altrimenti il calendario mostrerebbe per
 * sempre una scadenza già chiusa.
 */
export function invoicePaymentItem(invoice: InvoiceInterface): BoardItem | null {
  if (invoice.is_paid) {
    return null;
  }
  const dayKey = toDayKey(invoice.payment_date);
  if (!dayKey) {
    return null;
  }
  return {
    key: `INVOICE_PAYMENT-${invoice.id}`,
    kind: 'INVOICE_PAYMENT',
    category: 'FATTURE',
    dayKey,
    title: invoice.company?.company_name || 'Incasso atteso',
    subtitle: `Incasso fattura n. ${invoice.progressive_number}`,
    amount: toAmount(invoice.gross_amount),
    icon: 'account_balance_wallet',
    settled: false,
    important: false,
    invoice,
  };
}

/**
 * Incasso avvenuto: `marked_paid_at` è il momento in cui la fattura è stata
 * segnata come incassata, quindi il giorno reale dell'entrata, che può non
 * coincidere con la scadenza prevista.
 *
 * È un DateTimeField: va convertito in Date prima della chiave giornaliera,
 * perché l'istante arriva in UTC e il giorno va letto nel fuso dell'utente.
 */
export function invoiceSettledPaymentItem(
  invoice: InvoiceInterface,
): BoardItem | null {
  if (!invoice.is_paid || !invoice.marked_paid_at) {
    return null;
  }
  const dayKey = toDayKey(new Date(invoice.marked_paid_at));
  if (!dayKey) {
    return null;
  }
  return {
    key: `INVOICE_PAYMENT-${invoice.id}`,
    kind: 'INVOICE_PAYMENT',
    category: 'FATTURE',
    dayKey,
    title: invoice.company?.company_name || 'Incasso',
    subtitle: `Incassata fattura n. ${invoice.progressive_number}`,
    amount: toAmount(invoice.gross_amount),
    icon: 'account_balance_wallet',
    settled: true,
    important: false,
    invoice,
  };
}

export function fiscalEventItem(event: FiscalEventInterface): BoardItem | null {
  const dayKey = toDayKey(event.date);
  if (!dayKey) {
    return null;
  }
  return {
    key: `FISCAL-${event.id}`,
    kind: 'FISCAL',
    category: 'FISCO',
    dayKey,
    title: event.name,
    subtitle: event.description,
    amount: toAmount(event.amount),
    icon: event.is_credit ? 'savings' : 'gavel',
    settled: event.is_paid,
    important: false,
    fiscalEvent: event,
  };
}

export function personalEventItem(
  event: PersonalEventInterface,
): BoardItem | null {
  const dayKey = toDayKey(event.date);
  if (!dayKey) {
    return null;
  }
  return {
    key: `PERSONAL-${event.id}`,
    kind: 'PERSONAL',
    category: 'ATTIVITA',
    dayKey,
    title: event.title,
    subtitle: event.sub_title,
    icon: event.is_done
      ? 'task_alt'
      : event.is_important
        ? 'push_pin'
        : 'checklist',
    // Un'attività spuntata è "chiusa" esattamente come una scadenza pagata:
    // stessa resa barrata, stessa perdita di enfasi.
    settled: event.is_done,
    important: event.is_important && !event.is_done,
    personalEvent: event,
  };
}

/**
 * Ordine di lettura dentro una giornata: prima ciò che scade (fisco), poi gli
 * impegni personali, infine il movimento fatture.
 */
const KIND_WEIGHT: Record<BoardItemKind, number> = {
  FISCAL: 0,
  PERSONAL: 1,
  INVOICE_PAYMENT: 2,
  INVOICE_ISSUED: 3,
};

export function compareBoardItems(a: BoardItem, b: BoardItem): number {
  // Ciò che è chiuso (pagato o spuntato) scende in fondo: nella cella si
  // vedono solo le prime tre voci, che devono essere quelle ancora aperte.
  if (a.settled !== b.settled) {
    return a.settled ? 1 : -1;
  }
  if (a.important !== b.important) {
    return a.important ? -1 : 1;
  }
  if (KIND_WEIGHT[a.kind] !== KIND_WEIGHT[b.kind]) {
    return KIND_WEIGHT[a.kind] - KIND_WEIGHT[b.kind];
  }
  return a.title.localeCompare(b.title, 'it');
}
